import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Search, Building2, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_FORM = {
  razao_social: '',
  nome_fantasia: '',
  cnpj: '',
  tipo: 'Privado',
  esfera: '',
  poder: '',
  segmento: '',
  cidade: '',
  uf: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cep: '',
  observacoes: ''
};

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadClients = useCallback(async (searchParam = '') => {
    try {
      const response = await axios.get(`${API_URL}/clients`, {
        withCredentials: true,
        params: searchParam ? { search: searchParam } : {}
      });
      setClients(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const openCreate = () => {
    setEditingClient(null);
    setFormData({ ...EMPTY_FORM });
    setDialogOpen(true);
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setFormData({
      razao_social: client.razao_social || '',
      nome_fantasia: client.nome_fantasia || '',
      cnpj: client.cnpj || '',
      tipo: client.tipo || 'Privado',
      esfera: client.esfera || '',
      poder: client.poder || '',
      segmento: client.segmento || '',
      cidade: client.cidade || '',
      uf: client.uf || '',
      logradouro: client.logradouro || '',
      numero: client.numero || '',
      complemento: client.complemento || '',
      bairro: client.bairro || '',
      cep: client.cep || '',
      observacoes: client.observacoes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      if (editingClient) {
        await axios.put(`${API_URL}/clients/${editingClient.client_id}`, formData, { withCredentials: true });
        toast.success('Cliente atualizado com sucesso');
      } else {
        await axios.post(`${API_URL}/clients`, formData, { withCredentials: true });
        toast.success('Cliente criado com sucesso');
      }
      setDialogOpen(false);
      setEditingClient(null);
      setFormData({ ...EMPTY_FORM });
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cliente');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (clientId) => {
    try {
      await axios.delete(`${API_URL}/clients/${clientId}`, { withCredentials: true });
      toast.success('Cliente excluído com sucesso');
      setDeleteTarget(null);
      loadClients();
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingClient(null);
      setFormData({ ...EMPTY_FORM });
    }
  };

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Clientes</h1>
          <p className="text-[#6E6E73]">{"Gest\u00e3o de clientes"}</p>
        </div>
        <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-client-button">
          <Plus className="w-4 h-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5EA] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-5 h-5 text-[#6E6E73]" />
          <Input
            placeholder="Buscar por nome ou CNPJ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadClients()}
            className="flex-1"
            data-testid="client-search-input"
          />
          <Button onClick={loadClients} className="bg-[#AF2628] hover:bg-[#961B22]">Buscar</Button>
        </div>

        {clients.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client) => (
              <div key={client.client_id} className="border border-[#E5E5EA] rounded-lg p-4 hover:shadow-md hover:border-[#AF2628] transition-all" data-testid={`client-card-${client.client_id}`}>
                <div className="flex items-start gap-3">
                  <div className="bg-[#FFF1F0] w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[#AF2628]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#111111] truncate">{client.razao_social}</h3>
                    {client.nome_fantasia && <p className="text-sm text-[#6E6E73] truncate">{client.nome_fantasia}</p>}
                    <p className="text-xs text-[#AEAEB2] mt-2">{client.cnpj}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs bg-[#F7F7F8] px-2 py-1 rounded">{client.tipo}</span>
                      {client.tipo === 'Público' && client.esfera && (
                        <span className="text-xs bg-[#F0F9FF] px-2 py-1 rounded text-[#0EA5E9]">{client.esfera}</span>
                      )}
                      {client.tipo === 'Público' && client.poder && (
                        <span className="text-xs bg-[#F0FDF4] px-2 py-1 rounded text-[#22C55E]">{client.poder}</span>
                      )}
                      {client.segmento && <span className="text-xs bg-[#F7F7F8] px-2 py-1 rounded">{client.segmento}</span>}
                    </div>
                    {client.cidade && (
                      <p className="text-xs text-[#AEAEB2] mt-2">{client.cidade}{client.uf ? `/${client.uf}` : ''}</p>
                    )}
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(client)} className="text-xs" data-testid={`edit-client-${client.client_id}`}>
                        <Pencil className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDeleteTarget(client)} className="text-xs text-red-600 hover:text-red-700" data-testid={`delete-client-${client.client_id}`}>
                        <Trash2 className="w-3 h-3 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
            <p className="text-[#6E6E73]">Nenhum cliente encontrado</p>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="client-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>{"Raz\u00e3o Social *"}</Label>
                <Input required value={formData.razao_social} onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })} data-testid="client-razao-social-input" />
              </div>
              <div>
                <Label>Nome Fantasia</Label>
                <Input value={formData.nome_fantasia} onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })} />
              </div>
              <div>
                <Label>CNPJ *</Label>
                <Input required value={formData.cnpj} onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })} data-testid="client-cnpj-input" />
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select value={formData.tipo} onValueChange={(val) => setFormData({ ...formData, tipo: val, esfera: '', poder: '', segmento: '' })} data-testid="client-tipo-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Privado">Privado</SelectItem>
                    <SelectItem value="Público">{"P\u00fablico"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.tipo === 'Público' && (
                <>
                  <div>
                    <Label>Esfera *</Label>
                    <Select value={formData.esfera} onValueChange={(val) => setFormData({ ...formData, esfera: val })} data-testid="client-esfera-select">
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Federal">Federal</SelectItem>
                        <SelectItem value="Estadual">Estadual</SelectItem>
                        <SelectItem value="Municipal">Municipal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Poder *</Label>
                    <Select value={formData.poder} onValueChange={(val) => setFormData({ ...formData, poder: val })} data-testid="client-poder-select">
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Executivo">Executivo</SelectItem>
                        <SelectItem value="Legislativo">Legislativo</SelectItem>
                        <SelectItem value="Judiciário">{"Judici\u00e1rio"}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {formData.tipo === 'Privado' && (
                <div>
                  <Label>Segmento</Label>
                  <Input value={formData.segmento} onChange={(e) => setFormData({ ...formData, segmento: e.target.value })} />
                </div>
              )}

              <div>
                <Label>Logradouro</Label>
                <Input value={formData.logradouro} onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>{"N\u00famero"}</Label>
                  <Input value={formData.numero} onChange={(e) => setFormData({ ...formData, numero: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Complemento</Label>
                  <Input value={formData.complemento} onChange={(e) => setFormData({ ...formData, complemento: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Bairro</Label>
                <Input value={formData.bairro} onChange={(e) => setFormData({ ...formData, bairro: e.target.value })} />
              </div>
              <div>
                <Label>CEP</Label>
                <Input value={formData.cep} onChange={(e) => setFormData({ ...formData, cep: e.target.value })} />
              </div>
              <div>
                <Label>Cidade</Label>
                <Input value={formData.cidade} onChange={(e) => setFormData({ ...formData, cidade: e.target.value })} />
              </div>
              <div>
                <Label>UF</Label>
                <Input maxLength={2} value={formData.uf} onChange={(e) => setFormData({ ...formData, uf: e.target.value.toUpperCase() })} />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#AF2628] hover:bg-[#961B22]" disabled={submitting} data-testid="client-submit-button">
                {submitting ? 'Salvando...' : (editingClient ? 'Salvar' : 'Criar Cliente')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Confirmar Exclusão
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o cliente <strong>{deleteTarget?.razao_social}</strong>? O cliente não aparecerá mais nas listagens.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteTarget?.client_id)} data-testid="confirm-delete-client">
              Excluir Cliente
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
